import React, { ReactElement } from 'react';
import { Col, Divider, Row, Tooltip, Typography } from 'antd';
import { GetTeamResponse } from '@cbinsights/adminservice/adminservice';
import useHasEditAccess from 'client/modules/users/hooks/useHasEditAccess';
import { PermissionType } from '@cbinsights/adminpermissionservice/adminpermissionservice';
import {
  getSectionPage,
  useGetAdminPermissionsForUser,
} from 'client/utils/permissions-validation/hooks/UseGetAdminPermissionsForUserQuery';
import { teamPermissions } from 'client/modules/team/permissions/Team';
import { DomainWhiteList } from '../DomainWhitelist';
import MFA from '../MFA';
import LinkBasedSignup from '../TeamSettings/LinkBasedSignup';
import CopilotTeam from '../TeamType/CopilotTeam';
import HideTeam from '../HideTeam';
import ContractSeats from '../ContractSeats';

type Props = {
  mfaStatus?: boolean;
  domainWhitelist?: string;
  lbsStatus?: GetTeamResponse['signup_code'];
  refetchTeam: () => void;
  teamId: number;
  teamType: string;
  isHidden?: boolean;
  memberCap?: number;
  sfAccountId?: string;
};

export const TeamSettings = ({
  mfaStatus,
  domainWhitelist,
  lbsStatus,
  refetchTeam,
  teamId,
  teamType,
  isHidden,
  memberCap,
  sfAccountId,
}: Props): ReactElement => {
  const hasEditAccess = useHasEditAccess();

  const { data } = useGetAdminPermissionsForUser();
  const section = data
    ? getSectionPage(
        data,
        teamPermissions.pageId,
        teamPermissions.sections[6].sectionId
      )
    : null;

  const hasPrivilegedManagementAccess =
    section?.permissionTypes.includes(PermissionType.PUBLISH) ?? false;

  return (
    <Col
      xl={{ push: 2, span: 7 }}
      md={{ push: 2, span: 20 }}
      xs={{ push: 1, span: 22 }}
    >
      <Typography.Title level={4} className="!font-medium mt-4">
        Team Setting
      </Typography.Title>
      <Divider className="border-t-2 border-black border-solid" />
      <Row justify="space-between">
        <Col span="12">
          <Typography.Title level={5} className="!font-medium">
            Hide Team:
          </Typography.Title>
        </Col>
        <Col span="12" className="text-right pr-2">
          <HideTeam
            isHidden={isHidden}
            teamId={teamId}
            refetchTeam={refetchTeam}
            disabled={!hasPrivilegedManagementAccess}
          />
        </Col>
      </Row>
      <Row justify="space-between">
        <Col span="12">
          <Typography.Title level={5} className="!font-medium">
            <Tooltip title="Controls the MFA requirement when users sign in. If enabled, users will be sent a code via email upon sign in. Users must provide that code to sign in.">
              Multi-Factor Authentication:
            </Tooltip>
          </Typography.Title>
        </Col>
        <Col span="12" className="text-right pr-2">
          <MFA
            mfaStatus={mfaStatus}
            teamId={teamId}
            refetchTeam={refetchTeam}
            disabled={!hasEditAccess}
          />
        </Col>
      </Row>
      <Row justify="space-between">
        <Col span="12">
          <Typography.Title level={5} className="!font-medium">
            <Tooltip title="Enables a team to add new users to their team via a custom link.">
              Link Based Signup:
            </Tooltip>
          </Typography.Title>
        </Col>
        <Col span="12" style={{ textAlign: 'right' }} className="pr-2">
          <LinkBasedSignup
            refetchTeam={refetchTeam}
            teamId={teamId}
            lbsStatus={lbsStatus}
            domainWhitelist={domainWhitelist}
            disabled={!hasEditAccess}
          />
        </Col>
      </Row>
      <Row justify="space-between">
        <Col span="12">
          <Typography.Title level={5} className="!font-medium">
            <Tooltip title="The number of seats this team is contracted for. When the team is linked to a Salesforce account, this value is synced nightly from Salesforce.">
              Contract Seats:
            </Tooltip>
          </Typography.Title>
        </Col>
        <Col span="12" className="flex justify-end items-center">
          <ContractSeats
            memberCap={memberCap}
            teamId={teamId}
            sfAccountId={sfAccountId}
            refetchTeam={refetchTeam}
            disabled={!hasEditAccess}
          />
        </Col>
      </Row>
      <Row justify="space-between">
        <Col span="12">
          <Typography.Title level={5} className="!font-medium">
            <Tooltip title="">Type:</Tooltip>
          </Typography.Title>
        </Col>
        <Col span="12" style={{ textAlign: 'right' }} className="pr-2 mt-2">
          <CopilotTeam
            teamType={teamType}
            teamId={teamId}
            refetchTeam={refetchTeam}
            disabled={!hasEditAccess}
          />
        </Col>
      </Row>
      <Row justify="space-between">
        <Col span="12">
          <Typography.Title level={5} className="!font-medium">
            <Tooltip title="Sets the allowed email domains for users on this team. When signing up via Link Based Signup or Invitation, the user's email must match one of these domains.">
              Domain Whitelist:
            </Tooltip>
          </Typography.Title>
        </Col>
        <Col span="12" className="flex justify-end items-center">
          <DomainWhiteList
            domainWhitelist={domainWhitelist}
            refetchTeam={refetchTeam}
            teamId={teamId}
            disabled={!hasEditAccess}
          />
        </Col>
      </Row>
    </Col>
  );
};
